import React, { Children } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
export function PrivacyPolicy() {
  useDocumentMeta({
    title: 'Privacy Policy | VCA Capital Advisors',
    description: 'VCA Capital Advisors privacy policy. How we collect, use, and protect the personal and business information of our clients.',
    path: '/privacy'
  });
  return <div className="min-h-screen bg-black text-white font-sans selection:bg-white/20" data-id="element-544">
      <Navigation data-id="element-545" />

      <main className="pt-32 pb-24 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-546">
        <div className="mb-12" data-id="element-547">
          <h1 className="text-4xl md:text-5xl font-medium mb-4 tracking-tight" data-id="element-548">
            Privacy Policy
          </h1>
          <p className="text-zinc-400" data-id="element-549">
            Last Updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>
        </div>

        <div className="space-y-10 text-zinc-300 leading-relaxed" data-id="element-550">
          <section data-id="element-551">
            <p data-id="element-552">
              VCA Capital Advisors Inc. ("we," "our," or "us") is committed to
              protecting your privacy. This Privacy Policy explains how we
              collect, use, disclose, and safeguard your information when you
              visit our website or use our business funding brokerage and
              advisory services.
            </p>
          </section>

          <section data-id="element-553">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-554">
              1. Information We Collect
            </h2>
            <p className="mb-3" data-id="element-555">
              We may collect personal and business information that you
              voluntarily provide to us, including but not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-556">
              <li data-id="element-557">
                Contact information (name, email address, phone number, mailing
                address)
              </li>
              <li data-id="element-558">
                Business information (company name, industry, time in business,
                revenue)
              </li>
              <li data-id="element-559">Financial information necessary for funding applications</li>
              <li data-id="element-560">
                Information provided through forms, applications, or
                communications with us
              </li>
            </ul>
          </section>

          <section data-id="element-561">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-562">
              2. How We Use Your Information
            </h2>
            <p className="mb-3" data-id="element-563">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-564">
              <li data-id="element-565">Provide, operate, and maintain our services</li>
              <li data-id="element-566">Process and evaluate your funding applications</li>
              <li data-id="element-567">
                Communicate with you regarding your application, account, or our
                services
              </li>
              <li data-id="element-568">
                Send you administrative information, such as updates to our
                terms or policies
              </li>
              <li data-id="element-569">Respond to your inquiries and provide customer support</li>
              <li data-id="element-570">Improve our website and services</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 p-6 rounded-xl border border-zinc-800" data-id="element-571">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-572">
              3. SMS/Text Messaging Consent and Policy
            </h2>
            <p className="mb-3" data-id="element-573">
              By providing your phone number, you opt in to receive SMS messages
              from VCA Capital Advisors Inc. regarding your funding inquiry and
              application status.
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-3" data-id="element-574">
              <li data-id="element-575">
                <strong data-id="element-576">Message Frequency:</strong> Message frequency may vary
                based on your interaction with our services.
              </li>
              <li data-id="element-577">
                <strong data-id="element-578">Rates:</strong> Message and data rates may apply
                depending on your carrier plan.
              </li>
              <li data-id="element-579">
                <strong data-id="element-580">Opt-Out:</strong> You can opt out at any time by texting{' '}
                <strong data-id="element-581">STOP</strong> to our messages.
              </li>
              <li data-id="element-582">
                <strong data-id="element-583">Help:</strong> For assistance, text{' '}
                <strong data-id="element-584">HELP</strong> or contact us directly.
              </li>
              <li data-id="element-585">
                <strong data-id="element-586">No Marketing Sharing:</strong> We do not share phone
                numbers or SMS consent with third parties for marketing
                purposes.
              </li>
              <li data-id="element-587">
                <strong data-id="element-588">Not a Condition:</strong> SMS consent is not a condition
                of purchase or receiving our services.
              </li>
            </ul>
          </section>

          <section data-id="element-589">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-590">
              4. Data Sharing and Disclosure
            </h2>
            <p className="mb-3" data-id="element-591">
              We may share your information in the following situations:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-592">
              <li data-id="element-593">
                <strong data-id="element-594">With Funding Partners:</strong> We share necessary
                business and financial information with our network of lenders
                and funding partners to secure capital for your business.
              </li>
              <li data-id="element-595">
                <strong data-id="element-596">Service Providers:</strong> We may share information
                with third-party vendors who perform services on our behalf
                (e.g., hosting, data analysis, customer service).
              </li>
              <li data-id="element-597">
                <strong data-id="element-598">Legal Requirements:</strong> We may disclose your
                information if required by law, court order, or governmental
                regulation.
              </li>
            </ul>
            <p className="mt-3 font-medium text-white" data-id="element-599">
              Note: As stated above, mobile information and SMS consent will not
              be shared with third parties or affiliates for marketing or
              promotional purposes.
            </p>
          </section>

          <section data-id="element-600">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-601">
              5. Data Security
            </h2>
            <p data-id="element-602">
              We implement reasonable administrative, technical, and physical
              security measures to protect your personal information. However,
              no electronic transmission over the internet or information
              storage technology can be guaranteed to be 100% secure, so we
              cannot promise or guarantee that hackers, cybercriminals, or other
              unauthorized third parties will not be able to defeat our security
              and improperly collect, access, steal, or modify your information.
            </p>
          </section>

          <section data-id="element-603">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-604">
              6. Cookies and Tracking
            </h2>
            <p data-id="element-605">
              We may use cookies, web beacons, tracking pixels, and other
              tracking technologies on our website to help customize the site
              and improve your experience. You can choose to disable cookies
              through your browser settings, but doing so may affect your
              ability to use certain features of our website.
            </p>
          </section>

          <section data-id="element-606">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-607">
              7. Your Rights
            </h2>
            <p data-id="element-608">
              Depending on your location (such as under the CCPA or TCPA), you
              may have certain rights regarding your personal information,
              including the right to access, correct, or delete your data. To
              exercise these rights, please contact us using the information
              provided below.
            </p>
          </section>

          <section data-id="element-609">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-610">
              8. Children's Privacy
            </h2>
            <p data-id="element-611">
              Our services are intended for businesses and business owners. We
              do not knowingly solicit information from or market to children
              under the age of 18. If we learn that we have collected personal
              information from a child under age 18, we will delete that
              information as quickly as possible.
            </p>
          </section>

          <section data-id="element-612">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-613">
              9. Changes to This Policy
            </h2>
            <p data-id="element-614">
              We may update this Privacy Policy from time to time. The updated
              version will be indicated by an updated "Last Updated" date and
              the updated version will be effective as soon as it is accessible.
              We encourage you to review this Privacy Policy frequently to be
              informed of how we are protecting your information.
            </p>
          </section>

          <section data-id="element-615">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-616">
              10. Contact Us
            </h2>
            <p className="mb-4" data-id="element-617">
              If you have questions or comments about this Privacy Policy,
              please contact us at:
            </p>
            <div className="bg-zinc-900/30 p-6 rounded-xl border border-zinc-800" data-id="element-618">
              <p className="font-medium text-white mb-1" data-id="element-619">
                VCA Capital Advisors Inc.
              </p>
              <p data-id="element-620">2248 Broadway STE 1784</p>
              <p data-id="element-621">New York, NY 10024</p>
              <p className="mt-2" data-id="element-622">
                Email:{' '}
                <a href="mailto:info@vcacapitaladvisors.com" className="text-white hover:underline" data-id="element-623">
                  info@vcacapitaladvisors.com
                </a>
              </p>
              <p data-id="element-624">
                Phone:{' '}
                <a href="tel:646-331-2752" className="text-white hover:underline" data-id="element-625">
                  (646) 331-2752
                </a>
              </p>
            </div>
          </section>
        </div>
      </main>

      <Footer data-id="element-626" />
    </div>;
}